import React, { useState, useEffect, useCallback } from 'react';
import { Sidebar } from './Dashboard';
import { API } from '../App';

// On-property edition only: the 30-day trial, the product key and the plan
// limit. Takes the place of the Billing page (there is no Stripe on property).
const styles = {
  container: { minHeight: '100vh', backgroundColor: '#0f0f1a', display: 'flex' },
  main: { flex: 1, padding: '40px', overflowY: 'auto', maxWidth: '900px' },
  pageTitle: { color: '#ffffff', fontSize: '28px', fontWeight: 'bold', marginBottom: '8px' },
  pageSubtitle: { color: '#a0a0b0', fontSize: '14px', marginBottom: '32px' },
  card: { backgroundColor: '#16213e', borderRadius: '16px', padding: '24px', marginBottom: '24px' },
  cardTitle: { color: '#ffffff', fontSize: '16px', fontWeight: 'bold', marginBottom: '20px' },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', gap: '16px' },
  label: { color: '#a0a0b0', fontSize: '13px' },
  value: { color: '#ffffff', fontSize: '14px', fontWeight: 'bold', textAlign: 'right' },
  mono: { color: '#ffffff', fontSize: '16px', fontWeight: 'bold', fontFamily: 'Consolas, monospace', letterSpacing: '1px' },
  banner: { borderRadius: '12px', padding: '16px 20px', marginBottom: '24px', fontSize: '14px', lineHeight: '1.5' },
  bannerOk: { backgroundColor: '#0a2a0a', border: '1px solid #00cc00', color: '#9eff9e' },
  bannerWarn: { backgroundColor: '#2a1a0a', border: '1px solid #ffaa00', color: '#ffcc66' },
  bannerError: { backgroundColor: '#2a0a0a', border: '1px solid #e94560', color: '#ff8095' },
  meterTrack: { width: '100%', height: '10px', backgroundColor: '#0f3460', borderRadius: '6px', overflow: 'hidden', marginTop: '8px' },
  meterFill: h => ({ height: '100%', width: `${h}%`, backgroundColor: h >= 90 ? '#e94560' : '#4a9eff' }),
  textarea: {
    width: '100%', minHeight: '90px', padding: '12px', backgroundColor: '#0f3460', border: '1px solid #1a4a80',
    borderRadius: '10px', color: '#ffffff', fontFamily: 'Consolas, monospace', fontSize: '13px', boxSizing: 'border-box',
  },
  primaryBtn: {
    padding: '12px 24px', backgroundColor: '#e94560', border: 'none', borderRadius: '10px',
    color: '#ffffff', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', marginRight: '12px',
  },
  secondaryBtn: {
    display: 'inline-block', padding: '11px 24px', backgroundColor: 'transparent', border: '1px solid #4a9eff',
    borderRadius: '10px', color: '#4a9eff', fontSize: '14px', cursor: 'pointer',
  },
  smallBtn: {
    padding: '6px 12px', backgroundColor: 'transparent', border: '1px solid #0f3460', borderRadius: '8px',
    color: '#a0a0b0', fontSize: '12px', cursor: 'pointer', marginLeft: '12px',
  },
  note: { color: '#a0a0b0', fontSize: '13px', marginTop: '16px', lineHeight: '1.6' },
};

function fmtDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function License() {
  const token = localStorage.getItem('admin_token');
  const [status, setStatus] = useState(null);
  const [key, setKey] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/billing/status`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) { window.location.href = '/login'; return; }
      if (!res.ok) { setError('Could not load the license.'); return; }
      setStatus(await res.json());
    } catch {
      setError('Could not connect to the EZ Pace server.');
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  async function submit(text) {
    setBusy(true); setError(''); setDone('');
    try {
      const res = await fetch(`${API}/license/key`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ key: text }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(typeof data.detail === 'string' ? data.detail : 'That product key was not accepted.');
      } else {
        setDone('Product key loaded. ' + data.message);
        setKey('');
        try { sessionStorage.removeItem('ezpace_license'); } catch { /* ignore */ }
        window.dispatchEvent(new Event('ezpace-license-changed'));   // refreshes the menu's notice
        await load();
      }
    } catch {
      setError('Could not connect to the EZ Pace server.');
    }
    setBusy(false);
  }

  function fromFile(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => submit(String(reader.result || ''));
    reader.readAsText(file);
  }

  function copyId() {
    if (!status || !navigator.clipboard) return;
    navigator.clipboard.writeText(status.license.install_id).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const lic = status && status.license;
  const usage = status && status.usage;
  const pct = usage && usage.cap ? Math.min(100, Math.round((usage.current / usage.cap) * 100)) : 0;
  const bannerStyle = !lic ? null : lic.read_only ? styles.bannerError : lic.notice ? styles.bannerWarn : styles.bannerOk;

  return (
    <div style={styles.container}>
      <Sidebar active="License" />
      <div style={styles.main}>
        <div style={styles.pageTitle}>License</div>
        <div style={styles.pageSubtitle}>Your EZ Pace trial, product key and plan.</div>

        {error && <div style={{ ...styles.banner, ...styles.bannerError }}>{error}</div>}
        {done && <div style={{ ...styles.banner, ...styles.bannerOk }}>{done}</div>}
        {lic && <div style={{ ...styles.banner, ...bannerStyle }}>{lic.message}</div>}

        {lic && (
          <div style={styles.card}>
            <div style={styles.cardTitle}>This installation</div>
            <div style={styles.row}>
              <span style={styles.label}>Installation ID</span>
              <span>
                <span style={styles.mono}>{lic.install_id}</span>
                <button style={styles.smallBtn} onClick={copyId}>{copied ? 'Copied' : 'Copy'}</button>
              </span>
            </div>
            <div style={styles.row}>
              <span style={styles.label}>Status</span>
              <span style={styles.value}>{lic.kind === 'trial' ? 'Trial' : 'Licensed'}{lic.read_only ? ' (read-only)' : ''}</span>
            </div>
            <div style={styles.row}>
              <span style={styles.label}>{lic.kind === 'trial' ? 'Trial ends' : 'Product key good through'}</span>
              <span style={styles.value}>{fmtDate(lic.expires)}</span>
            </div>
            <div style={styles.row}>
              <span style={styles.label}>Plan</span>
              <span style={styles.value}>{lic.plan_name}</span>
            </div>
            {lic.pending_key && (
              <div style={styles.row}>
                <span style={styles.label}>Next product key</span>
                <span style={styles.value}>Starts {fmtDate(lic.pending_key.starts)}</span>
              </div>
            )}
            {usage && (
              <div style={{ marginTop: '16px' }}>
                <div style={styles.row}>
                  <span style={styles.label}>Dealers + observers</span>
                  <span style={styles.value}>{usage.current}{usage.cap ? ` of ${usage.cap}` : ''}</span>
                </div>
                {usage.cap ? <div style={styles.meterTrack}><div style={styles.meterFill(pct)} /></div> : null}
              </div>
            )}
          </div>
        )}

        <div style={styles.card}>
          <div style={styles.cardTitle}>Load a product key</div>
          <textarea
            style={styles.textarea}
            value={key}
            onChange={e => setKey(e.target.value)}
            placeholder="Paste the product key here. It starts with SCS1-"
          />
          <div style={{ marginTop: '16px' }}>
            <button style={styles.primaryBtn} disabled={busy || !key.trim()} onClick={() => submit(key)}>
              {busy ? 'Checking...' : 'Load Product Key'}
            </button>
            <label style={styles.secondaryBtn}>
              Load from File...
              <input type="file" accept=".txt" style={{ display: 'none' }} onChange={fromFile} />
            </label>
          </div>
          <div style={styles.note}>
            To get a product key, send the Installation ID above to Sterling Casino Support. The key comes as a
            file (EZPace-License.txt): load it with Load from File, or paste the key. When a trial or product key
            ends, EZ Pace becomes read-only: reports and past reviews keep working and nothing is deleted.
          </div>
        </div>
      </div>
    </div>
  );
}

export default License;
